import { Link } from 'react-router';
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import DashboardLayout from '../components/DashboardLayout';
import StatusBadge from '../components/partials/StatusBadge';
import SectionHeader from '../components/partials/SectionHeader';
import ServiceIconBox from '../components/partials/ServiceIconBox';
import { exploreServices } from '../constants/exploreServices';
import { useServiceRequests } from '../hooks/useServiceRequests';
import { useBusinessEntities } from '../hooks/useBusinessEntities';
import { useTradingStatus } from '../hooks/useTradingStatus';
import { useLoginProfileContext } from '../contexts/LoginProfileContext';
import {
  serviceRequestBadge,
  statusBadge,
  formatStatusDisplay,
} from '../constants/statusBadge';
import {
  getLlcEntityUrl,
  getServiceRequestUrl,
  isUnfinishedIntakePurchase,
  unfinishedIntakePurchaseTitle,
  unfinishedIntakePurchaseTypeLabel,
} from '../constants/llcLinks';

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function DashboardPage() {
  const activeRequests = useServiceRequests();
  const businessEntities = useBusinessEntities();
  const tradingStatus = useTradingStatus();
  const loginProfile = useLoginProfileContext();

  const isNewYorkResident = loginProfile.profile?.residenceState === 'NY';

  const hasCompletedLlc =
    !businessEntities.loading &&
    !businessEntities.error &&
    businessEntities.entities.some((e) => {
      const s = e.status?.toLowerCase();
      return (
        s === 'active' ||
        s === 'import completed' ||
        s === 'importcompleted' ||
        s === 'imported'
      );
    });

  const hasCompletedTrading =
    !tradingStatus.loading &&
    !tradingStatus.error &&
    tradingStatus.status?.tradingState === 'Approved';

  const isNonUsCitizen =
    !tradingStatus.loading &&
    !tradingStatus.error &&
    tradingStatus.status?.tradingState === 'IneligibleCitizenship';

  return (
    <DashboardLayout>
      <div className="space-y-6 py-5 pl-14 pr-4 md:space-y-9 md:px-10 md:py-8">
        {/* Active Requests */}
        <section>
          <SectionHeader>Active Requests</SectionHeader>
          <div className="dash-card overflow-hidden rounded-[10px]">
            {activeRequests.loading && (
              <div className="dash-row px-4 py-4 md:px-[22px] md:py-[18px]">
                <p className="dash-row__meta text-[13px]">Loading...</p>
              </div>
            )}

            {!activeRequests.loading && activeRequests.error && (
              <div className="dash-row px-4 py-4 md:px-[22px] md:py-[18px]">
                <p className="dash-row__meta text-[13px]">
                  Could not load your active requests. Please try again later.
                </p>
              </div>
            )}

            {!activeRequests.loading &&
              !activeRequests.error &&
              activeRequests.requests.length === 0 && (
                <div className="dash-row px-4 py-4 md:px-[22px] md:py-[18px]">
                  <p className="dash-row__meta text-[13px]">
                    You have no active requests yet.
                  </p>
                </div>
              )}

            {!activeRequests.loading &&
              !activeRequests.error &&
              activeRequests.requests.map((req) => {
                const badge = serviceRequestBadge(req.status, req.needsPayment);
                const businessName =
                  req.businessName && req.businessName !== '—'
                    ? req.businessName
                    : null;
                const href = getServiceRequestUrl(req);
                const rowClassName =
                  'dash-row flex flex-col gap-2 px-4 py-4 md:flex-row md:items-center md:justify-between md:gap-4 md:px-[22px] md:py-[18px]';
                const rowContent = (
                  <>
                    <div>
                      <p className="dash-row__title inline-flex items-center gap-1.5 font-serif text-[16px] font-medium leading-snug md:text-[19px]">
                        {req.serviceTypeName}
                        {href && (
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0 text-on-surface-tertiary" />
                        )}
                      </p>
                      <p className="dash-row__meta mt-0.5 text-[13px]">
                        {businessName ?? (
                          <span className="italic font-normal text-on-surface-tertiary">
                            Unnamed Business
                          </span>
                        )}
                      </p>
                    </div>
                    <StatusBadge
                      variant={badge.variant}
                      label={badge.label}
                      className="self-start"
                    />
                  </>
                );
                return href ? (
                  <a
                    key={req.serviceRequestId}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className={rowClassName}
                  >
                    {rowContent}
                  </a>
                ) : (
                  <div key={req.serviceRequestId} className={rowClassName}>
                    {rowContent}
                  </div>
                );
              })}
          </div>
        </section>

        {/* Business Entities */}
        <section>
          <SectionHeader>Business Entities</SectionHeader>
          <div className="dash-card overflow-hidden rounded-[10px]">
            {businessEntities.loading && (
              <div className="dash-row px-4 py-4 md:px-[22px] md:py-[18px]">
                <p className="dash-row__meta text-[13px]">Loading...</p>
              </div>
            )}

            {!businessEntities.loading && businessEntities.error && (
              <div className="dash-row px-4 py-4 md:px-[22px] md:py-[18px]">
                <p className="dash-row__meta text-[13px]">
                  Could not load your business entities. Please try again later.
                </p>
              </div>
            )}

            {!businessEntities.loading &&
              !businessEntities.error &&
              businessEntities.entities.length === 0 && (
                <div className="dash-row px-4 py-4 md:px-[22px] md:py-[18px]">
                  <p className="dash-row__meta text-[13px]">
                    You have no business entities yet.
                  </p>
                </div>
              )}

            {!businessEntities.loading &&
              !businessEntities.error &&
              businessEntities.entities.map((entity) => {
                // A paid-but-unfinished purchase has no business to name yet, so
                // "Unnamed Business · Incomplete" would read as a glitch rather than
                // as the one row here the client still has to act on.
                const isUnfinishedPurchase = isUnfinishedIntakePurchase(entity);
                const name = entity.doingBusinessAs || entity.legalName || null;
                const date = formatDate(
                  entity.importDocumentsSavedAt ??
                    entity.formationDate ??
                    entity.createdAt
                );
                const parts = isUnfinishedPurchase
                  ? [
                      unfinishedIntakePurchaseTypeLabel(entity.serviceTypeName),
                      date && `Paid ${date}`,
                    ]
                  : [
                      entity.serviceTypeName,
                      entity.status ? formatStatusDisplay(entity.status) : null,
                      date,
                    ];
                const subtitle = parts.filter(Boolean).join(' · ');
                const badge = entity.status ? statusBadge(entity.status) : null;
                const href = getLlcEntityUrl(entity);
                const rowClassName =
                  'dash-row flex flex-col gap-2 px-4 py-4 md:flex-row md:items-center md:justify-between md:gap-4 md:px-[22px] md:py-[18px]';
                const rowContent = (
                  <>
                    <div>
                      <p className="dash-row__title inline-flex items-center gap-1.5 font-serif text-[16px] font-medium leading-snug md:text-[19px]">
                        {isUnfinishedPurchase
                          ? unfinishedIntakePurchaseTitle(
                              entity.serviceTypeName
                            )
                          : name || (
                              <span className="italic font-normal text-on-surface-tertiary">
                                Unnamed Business
                              </span>
                            )}
                        {href && (
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0 text-on-surface-tertiary" />
                        )}
                      </p>
                      {subtitle && (
                        <p className="dash-row__meta mt-0.5 text-[13px]">
                          {subtitle}
                        </p>
                      )}
                    </div>
                    {isUnfinishedPurchase ? (
                      <span className="dash-service-card__cta inline-flex items-center gap-1 self-start whitespace-nowrap text-[11px] font-semibold uppercase md:self-center">
                        Continue
                        <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                      </span>
                    ) : (
                      badge && (
                        <StatusBadge
                          variant={badge.variant}
                          label={badge.label}
                          className="self-start"
                        />
                      )
                    )}
                  </>
                );
                // Namespaced per id kind: the three kinds number independently, so bare ids
                // would collide across rows (a business #5 and a service request #5).
                const key =
                  entity.businessEntityId != null
                    ? `entity-${entity.businessEntityId}`
                    : entity.serviceRequestId != null
                      ? `sr-${entity.serviceRequestId}`
                      : entity.intakePurchaseId != null
                        ? `purchase-${entity.intakePurchaseId}`
                        : entity.createdAt;
                return href ? (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className={rowClassName}
                  >
                    {rowContent}
                  </a>
                ) : (
                  <div key={key} className={rowClassName}>
                    {rowContent}
                  </div>
                );
              })}
            {!businessEntities.loading &&
              !businessEntities.error &&
              businessEntities.entities.length > 0 && (
                <a
                  href={`${process.env.REACT_APP_LLC_URL || 'http://localhost:3000/llc'}/intake`}
                  target="_blank"
                  rel="noreferrer"
                  className="dash-row flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between md:gap-4 md:px-[22px] md:py-[18px]"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-dashed border-[var(--dash-border-strong)] text-[var(--dash-text-muted)]">
                      <PlusIcon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="dash-row__title font-serif text-[16px] font-medium leading-snug md:text-[19px]">
                        Add another LLC
                      </p>
                      <p className="dash-row__meta mt-0.5 text-[13px]">
                        Form an additional entity — you're already set up, so
                        it's quick
                      </p>
                    </div>
                  </div>
                  <span className="dash-service-card__cta inline-flex self-center items-center gap-1 whitespace-nowrap text-[11px] font-semibold uppercase">
                    Start Setup
                    <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </span>
                </a>
              )}
          </div>
        </section>

        {/* Explore Services */}
        <section>
          <SectionHeader>Explore Services</SectionHeader>
          <div className="flex flex-col gap-3.5">
            {exploreServices.map((service) => {
              const isCompletedLlcCard =
                service.id === 'form-llc' && hasCompletedLlc;
              const isCompletedTradingCard =
                service.id === 'dag-trading' && hasCompletedTrading;
              const isCompletedCard =
                isCompletedLlcCard || isCompletedTradingCard;
              const Icon = isCompletedCard ? CheckIcon : service.Icon;
              const tagline = isCompletedLlcCard
                ? 'Your LLC is active — everything else is unlocked'
                : isCompletedTradingCard
                  ? 'Your trading account is active and live'
                  : service.subtitle;
              const body = isCompletedLlcCard
                ? 'Your Wyoming LLC is active and in good standing. Trading is now available to set up.'
                : isCompletedTradingCard
                  ? 'Your Uphold trading account is live under your LLC. You can now trade crypto directly through your entity.'
                  : service.body;

              return (
                <div
                  key={service.title}
                  className="dash-service-card grid gap-4 rounded-[10px] p-4 md:gap-8 md:p-6"
                >
                  {/* Left: icon + name + tagline + CTA (desktop only) */}
                  <div className="dash-service-card__head grid gap-3.5">
                    <ServiceIconBox
                      Icon={Icon}
                      variant={isCompletedCard ? 'completed' : undefined}
                    />
                    <div>
                      <p className="dash-service-card__title font-serif text-[18px] font-medium leading-tight md:text-[21px]">
                        {service.title}
                      </p>
                      <p className="dash-service-card__tagline mb-3.5 mt-1 text-[13.5px] leading-snug">
                        {tagline}
                      </p>
                      {/* CTA visible on desktop only */}
                      {isCompletedLlcCard ? (
                        <span className="dash-llc-active-pill hidden md:inline-flex">
                          ✓ LLC active
                        </span>
                      ) : isCompletedTradingCard ? (
                        <span className="dash-llc-active-pill hidden md:inline-flex">
                          ✓ Trading active
                        </span>
                      ) : service.internal ? (
                        <Link
                          to={service.href}
                          className="dash-service-card__cta hidden items-center gap-1.5 text-[11px] font-semibold uppercase md:inline-flex"
                        >
                          {service.action} →
                        </Link>
                      ) : (
                        <a
                          href={service.href || '#'}
                          target="_blank"
                          rel="noreferrer"
                          className="dash-service-card__cta hidden items-center gap-1.5 text-[11px] font-semibold uppercase md:inline-flex"
                        >
                          {service.action}
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0 -translate-y-px" />
                        </a>
                      )}
                      {service.id === 'dag-trading' && isNonUsCitizen && (
                        <p className="mt-2 flex items-center gap-1.5 text-[12px] text-red-400">
                          <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
                          Not available to non-U.S. citizens
                        </p>
                      )}
                      {service.id === 'dag-trading' &&
                        !isCompletedTradingCard &&
                        isNewYorkResident && (
                          <p className="mt-2 flex items-center gap-1.5 text-[12px] text-red-400">
                            <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
                            Not available to New York residents
                          </p>
                        )}
                    </div>
                  </div>
                  {/* Right: description */}
                  <p className="dash-service-card__body text-[14px] leading-relaxed">
                    {body}
                  </p>
                  {/* CTA visible on mobile only — sits at card bottom */}
                  {isCompletedLlcCard ? (
                    <span className="dash-llc-active-pill inline-flex w-full justify-center md:hidden">
                      ✓ LLC active
                    </span>
                  ) : isCompletedTradingCard ? (
                    <span className="dash-llc-active-pill inline-flex w-full justify-center md:hidden">
                      ✓ Trading active
                    </span>
                  ) : service.internal ? (
                    <Link
                      to={service.href}
                      className="dash-service-card__cta inline-flex w-full items-center justify-center gap-1.5 text-[11px] font-semibold uppercase md:hidden"
                    >
                      {service.action} →
                    </Link>
                  ) : (
                    <a
                      href={service.href || '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="dash-service-card__cta inline-flex w-full items-center justify-center gap-1.5 text-[11px] font-semibold uppercase md:hidden"
                    >
                      {service.action}
                      <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
